import { config } from '../config';
import { trackEvent } from '../utils/analytics';
import { createInstant } from './instant';

export const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export const getRequestSizeDetails = (config) => {
  let fileSize = 0;
  let textSize = 0;
  const files = [];
  const textData = [];

  try {
    if (config?.data instanceof FormData) {
      for (let pair of config.data.entries()) {
        if (pair[1] instanceof File) {
          fileSize += pair[1]?.size || 0;
          files.push({
            name: pair[1]?.name || 'unknown',
            size: pair[1]?.size || 0,
            formatted: formatBytes(pair[1]?.size || 0),
            type: pair[1]?.type || 'unknown'
          });
        } else if (typeof pair[1] === 'string') {
          const size = new Blob([pair[1]]).size;
          textSize += size;
          textData.push({
            key: pair[0] || 'unknown',
            size: size,
            formatted: formatBytes(size)
          });
        }
      }
    } else if (config?.data) {
      textSize = new Blob([JSON.stringify(config.data)]).size;
      textData.push({
        key: 'json_body',
        size: textSize,
        formatted: formatBytes(textSize)
      });
    }
  } catch (err) {
    console.warn('Error calculating request size:', err);
  }

  const totalSize = fileSize + textSize;

  return {
    files: {
      count: files?.length || 0,
      description: 'จำนวนไฟล์ที่ส่ง',
      totalSize: fileSize || 0,
      formatted: formatBytes(fileSize || 0),
      sizeDescription: 'ขนาดรวมทั้งหมดของไฟล์',
      details: files || [],
      detailsDescription: 'รายละเอียดของแต่ละไฟล์ (name, size, type)'
    },
    text: {
      totalSize: textSize || 0,
      formatted: formatBytes(textSize || 0),
      description: 'ขนาดรวมของข้อมูล text/JSON ที่ส่ง',
      details: textData || [],
      detailsDescription: 'รายละเอียดของข้อมูล text (key, size)'
    },
    total: {
      totalSize: totalSize || 0,
      formatted: formatBytes(totalSize || 0),
      description: `รวมทั้งหมด ${formatBytes(totalSize || 0)} (ไฟล์: ${formatBytes(fileSize || 0)} + ข้อมูล: ${formatBytes(textSize || 0)})`,
      breakdown: {
        filesSize: formatBytes(fileSize || 0),
        filesDescription: 'ขนาดไฟล์ทั้งหมด',
        textSize: formatBytes(textSize || 0),
        textDescription: 'ขนาดข้อมูลทั้งหมด'
      }
    }
  };
};

export const getErrorDescription = (errorType, status) => {
  if (errorType === 'SERVER_ERROR') {
    if (status === 401) return 'ไม่ได้รับการยืนยันตัวตน - ลองลงชื่อเข้าสู่ระบบใหม่';
    if (status === 403) return 'ไม่มีสิทธิ์เข้าถึง - ติดต่อผู้ดูแลระบบ';
    if (status === 404) return 'ไม่พบข้อมูล - ติดต่อ support';
    if (status === 500 || status === 503) return 'Server มีปัญหา - ลองใหม่ในอีกสักครู่';
    return `Server ส่งข้อผิดพลาด (Status ${status || 'unknown'}) - ลองใหม่หรือติดต่อ support`;
  }

  if (errorType === 'NETWORK_ERROR') {
    return 'เชื่อมต่อเน็ตไม่ได้ - ตรวจสอบ WiFi/เซลลูล่าร์ หรือพอร์ต API ในระบบ';
  }

  if (errorType === 'REQUEST_SETUP_ERROR') {
    return 'ตั้งค่า API ผิด - ตรวจสอบ URL, headers, หรือ baseURL ในระบบ';
  }

  return 'เกิดข้อผิดพลาด - ลองใหม่หรือติดต่อผู้ดูแลระบบ';
};

export const setupErrorInterceptor = (instance, connectorName) => {
  instance.interceptors.request.use(
    (config) => {
      if (config) {
        config.requestSizeDetails = getRequestSizeDetails(config);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => {
      if (response?.config?.requestSizeDetails) {
        trackEvent(
          'Eclaim Submit',
          `Success: API ${response?.config?.method?.toUpperCase()} ${response?.config?.url}`,
          {
            wrapSuccess: {
              connector: connectorName || 'unknown',
              method: response?.config?.method?.toUpperCase() || 'UNKNOWN',
              url: response?.config?.url || 'unknown',
              status: response?.status || 200,
              requestSize: response?.config?.requestSizeDetails,
              timestamp: new Date().toISOString(),
              environment: process.env.NODE_ENV || 'unknown'
            }
          }
        );
      }
      return response;
    },
    (error) => {
      const errorType = error?.response ? 'SERVER_ERROR' : error?.request ? 'NETWORK_ERROR' : 'REQUEST_SETUP_ERROR';

      const sizeDetails = error?.config?.requestSizeDetails || {
        files: { count: 0, description: 'จำนวนไฟล์', totalSize: 0, formatted: '0 Bytes', sizeDescription: 'ขนาดไฟล์', details: [] },
        text: { totalSize: 0, formatted: '0 Bytes', description: 'ขนาดข้อมูล text', details: [] },
        total: { totalSize: 0, formatted: '0 Bytes', description: 'รวมทั้งหมด', breakdown: { filesSize: '0 Bytes', textSize: '0 Bytes' } }
      };

      const errorDetails = {
        connector: connectorName || 'unknown',
        method: error?.config?.method?.toUpperCase() || 'UNKNOWN',
        url: error?.config?.url || 'unknown',
        status: error?.response?.status || null,
        errorType: errorType,
        errorDescription: getErrorDescription(errorType, error?.response?.status),
        errorCode: error?.code || 'UNKNOWN',
        errorMessage: error?.message || 'No error message',
        responseData: error?.response?.data || null,
        isFailedToFetch: !error?.response && !error?.message?.includes('timeout'),
        requestSize: sizeDetails,
        timestamp: new Date().toISOString(),
        environment: process.env.NODE_ENV || 'unknown',
        debugInfo: `Error เกิดจาก ${connectorName || 'unknown'} ที่เรียก ${error?.config?.method?.toUpperCase() || 'UNKNOWN'} ${error?.config?.url || 'unknown'} โปรดตรวจสอบ network, API endpoint, หรือ server status`
      };

      try {
        trackEvent(
          'Eclaim Submit',
          `Error: API ${error?.config?.method?.toUpperCase() || 'UNKNOWN'} ${error?.config?.url || 'unknown'} Failed`,
          { wrapError: errorDetails }
        );
      } catch (trackErr) {
        try {
          trackEvent(
            'Eclaim Submit',
            'Error: Analytics Tracking Failed',
            {
              wrapError: {
                connector: connectorName || 'unknown',
                method: error?.config?.method?.toUpperCase() || 'UNKNOWN',
                url: error?.config?.url || 'unknown',
                originalErrorType: errorType,
                trackingError: {
                  message: trackErr?.message || 'Unknown tracking error',
                  code: trackErr?.code || 'UNKNOWN',
                  description: 'ล้มเหลวในการ track event เนื่องจากปัญหาใน analytics service'
                },
                timestamp: new Date().toISOString(),
                environment: process.env.NODE_ENV || 'unknown',
                debugInfo: `ไม่สามารถ track error ได้เนื่องจาก ${trackErr?.message || 'unknown error'}`
              }
            }
          );
        } catch (fallbackErr) {
          // ถ้า trackEvent ครั้งที่ 2 ยังล้มเหลว ก็ log ออกมาเท่านั้น
          console.error('Critical: Failed to track both original error and tracking failure:', {
            originalError: error?.message,
            trackingError: trackErr?.message,
            fallbackError: fallbackErr?.message
          });
        }

        console.warn('Error tracking failed:', trackErr);
      }

      console.error(`[${connectorName || 'unknown'}] ${error?.config?.method?.toUpperCase() || 'UNKNOWN'} ${error?.config?.url || 'unknown'}:`, errorDetails);
      return Promise.reject(error);
    }
  );
};

export const pcbffConnector = createInstant({
  prefix: config?.api_endpoint || '',
  timeout: 60000,
  headers: { 'Content-type': 'application/json; charset=UTF-8' },
});

export const pcEkycConnector = createInstant({
  prefix: config?.ekyc_endpoint?.api || '',
  timeout: 35000,
  headers: {
    'Content-type': 'application/json; charset=UTF-8',
    Authorization: `Bearer ${config?.ekyc_endpoint?.token || ''}`,
  },
});
